import React from 'react'

import { Box, Checkbox, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { xor } from 'lodash'

import LabeledInput from 'modules/core/components/LabeledInput'
import i18n from 'modules/core/lib/i18n'

import { AnswerQuestion } from 'modules/questionnaires/components/Answer/AnswerQuestion'
import {
  isMultipleChoiceQuestion,
  isSingleChoiceQuestion,
} from 'modules/questionnaires/lib/helpers'
import {
  ChoiceAnswer as AnswerType,
  MultipleChoiceQuestion as QuestionType,
} from 'modules/questionnaires/lib/types'

import * as styles from './index.vanilla.css'

type Props = {
  question: QuestionType
  answer?: AnswerType
  position?: number
  readonly?: boolean
  onChange: (answer: AnswerType) => void
  hasAttemptedSubmit?: boolean
}

export const AnswerChoice: React.FC<Props> = ({
  question,
  answer,
  position,
  readonly = false,
  onChange,
  hasAttemptedSubmit = false,
}) => {
  const [choiceAnswers, setChoiceAnswers] = React.useState<string[]>(answer?.answerChoice || [])

  const isMultipleChoice = isMultipleChoiceQuestion(question)
  const isSingleChoice = isSingleChoiceQuestion(question)

  const notSelectable =
    question.maxChoices !== null && choiceAnswers.length === question.maxChoices && !isSingleChoice

  const isDisabled = (option: string, options: string[]) => {
    return readonly || (notSelectable && !options.includes(option))
  }

  const handleChange = (option: string) => {
    const isSingleChoiceAnswer = option === choiceAnswers[0]
    const singleAnswer = isSingleChoice && isSingleChoiceAnswer ? [] : [option]
    const selectedChoices = isSingleChoice ? singleAnswer : xor(choiceAnswers, [option])
    setChoiceAnswers(selectedChoices)
    onChange({ ...answer, uuid: question.uuid, answerChoice: selectedChoices })
  }

  const choiceOptions = question.choiceOptions
  const answersString = choiceAnswers.join(',')

  const isMandatoryAndEmpty =
    question.mandatory && (!choiceAnswers?.length || choiceAnswers.length === 0)

  return (
    <AnswerQuestion uuid={question.uuid} position={position} title={question.title}>
      <Box gap="s16">
        <Text.Normal size="200" color="grey800">
          {question.maxChoices === null
            ? i18n.t('questionnaires.answers.choice.all_options')
            : i18n.tp('questionnaires.answers.choice.max_options', {
                count: question.maxChoices,
              })}
        </Text.Normal>
        <Box
          gap="s8"
          role="group"
          aria-labelledby={`question-${question.uuid}`}
          className={styles.options}
        >
          {choiceOptions.map((option, i) => {
            const choiceOptionLabel = question?.choiceOptionsAbstraction?.find(
              (choice) => choice.id === option
            )?.value
            const checked = choiceAnswers.includes(option) ?? false
            const disabled = isDisabled(option, choiceAnswers || [])
            return (
              <LabeledInput
                key={option}
                mandatory={question.mandatory && i == 0}
                value={answersString}
                readonly={disabled}
                error={
                  hasAttemptedSubmit && isMandatoryAndEmpty
                    ? i18n.t(
                        'performance_process.reviews.answer_evaluation.missing_mandatory_field'
                      )
                    : undefined
                }
              >
                <Box
                  key={option}
                  flexDirection="row"
                  padding="s12"
                  gap="s8"
                  alignItems="center"
                  cursor="pointer"
                >
                  {isMultipleChoice && (
                    <Checkbox
                      size="medium"
                      value={option}
                      disabled={disabled}
                      checked={checked}
                      onChange={() => handleChange(option)}
                    />
                  )}
                  {isSingleChoice && (
                    <RadioButton
                      size="medium"
                      value={option}
                      disabled={disabled}
                      checked={checked}
                      onChange={() => handleChange(option)}
                    />
                  )}
                  <Text.Normal size="200">{choiceOptionLabel ?? option}</Text.Normal>
                </Box>
              </LabeledInput>
            )
          })}
        </Box>
      </Box>
    </AnswerQuestion>
  )
}
